import { Button, IconButton, Text } from '@chakra-ui/react'
import { arrayMove } from '@dnd-kit/sortable'
import {
  ContentNodeUtils,
  DataTypeConfigUtils,
  InputValueContentNode,
  ListContentNode,
} from '@helo/cms'
import { pipe } from '@helo/utils'
import { useMemo } from 'react'
import SimpleBar from 'simplebar-react'

import { useAppStore } from '../../store/appStore/appStore'
import {
  OrderByInputObjectContentNode,
  OrderByInputObjectDataTypeConfig,
  SortedSortItem,
  contentNodeToSortItems,
  createSortContentNode,
  getDirectionFromContentNode,
} from '../../widgets/TableWidget/sortEditor.utils'
import { XIcon } from '../Icons/XIcon'
import { SortableItem, SortableList } from '../drag-and-drop'
import { CustomDndContext as DndContext } from '../drag-and-drop/DndContext'
import { ButtonRadio } from '../inputs/ButtonRadio'
import { SelectInput } from '../inputs/SelectInput'
import { SelectInputOption } from '../inputs/SelectInput/SelectInput.parts'

type SortEditorProps = {
  orderByInputValueContentNode: InputValueContentNode
  onChange?: (contentNode: InputValueContentNode) => void
}

export const SortEditor = ({
  orderByInputValueContentNode,
  onChange,
}: SortEditorProps) => {
  if (!orderByInputValueContentNode) {
    throw new Error('orderByInputValueContentNode is required')
  }

  // Extract the list content node from the orderByInputValueContentNode.ofType
  const orderByListContentNode = useMemo(() => {
    return orderByInputValueContentNode.ofType as ListContentNode<OrderByInputObjectContentNode>
  }, [orderByInputValueContentNode])

  if (!orderByListContentNode) {
    throw new Error(
      'orderByInputValueContentNode.ofType must be a ListContentNode',
    )
  }

  const orderByListConfig = orderByListContentNode.getDataTypeConfig()
  if (!orderByListConfig) {
    throw new Error('orderByListConfig is required')
  }
  // Derive sort input config from orderByListConfig.ofType (INPUT_OBJECT)
  const orderByInputObjectConfig = useMemo(() => {
    return pipe(
      orderByListConfig,
      DataTypeConfigUtils.getOfType(),
    ) as OrderByInputObjectDataTypeConfig
  }, [orderByListConfig])

  const hasRole = useAppStore(store => store.auth.hasRole)

  // Get field configs from sortInputConfig fields
  // Create ContentNodes to access state.label properly
  const fieldConfigs = pipe(
    orderByInputObjectConfig,
    DataTypeConfigUtils.getFields(),
  )

  const filterFieldsSelectInputOptions: SelectInputOption[] = fieldConfigs
    .filter(field => field.state.sortDisabled !== true)
    .filter(field => hasRole(field.state.roles))
    .map(field => ({
      label: (field.state.label as string) ?? field.key,
      value: field.key,
    }))

  // Convert ContentNode to SortItems for internal use
  const sortItems = useMemo(
    () => contentNodeToSortItems(orderByListContentNode),
    [orderByListContentNode],
  )

  // Derive sortedColumns from props (no useState needed)
  const sortedColumns = useMemo<SortedSortItem[]>(() => {
    // Generate stable IDs based on index and propertyKey for consistent re-renders
    return sortItems.map((item, index) => ({
      ...item,
      id: `${item.propertyKey}-${index}`,
      sortOrder: index + 1,
    }))
  }, [sortItems])

  const handleSetSortedItems = (newSortedColumns: SortedSortItem[]) => {
    if (!onChange) {
      return
    }
    // Update the list content node with the new sorted items
    const updatedListContentNode = pipe(
      orderByListContentNode,
      ContentNodeUtils.updateChildContentNode(
        newSortedColumns.map(item => item.contentNode),
      ),
    )
    // Update the orderByInputValueContentNode with the updated list
    const updatedOrderByInputValueContentNode = pipe(
      orderByInputValueContentNode,
      ContentNodeUtils.updateChildContentNode(updatedListContentNode),
    )
    onChange(updatedOrderByInputValueContentNode as InputValueContentNode)
  }

  return (
    <DndContext
      onDragStart={() => {
        if (sortedColumns.length <= 1) return
      }}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over?.id) {
          const activeIndex = sortedColumns.findIndex(
            ({ id }) => id === active.id,
          )
          const overIndex = sortedColumns.findIndex(({ id }) => id === over.id)

          handleSetSortedItems(arrayMove(sortedColumns, activeIndex, overIndex))
        }
      }}
    >
      <Text
        marginLeft="45px"
        fontSize="12px"
        color="presets.neutral.90"
        fontWeight="600"
      >
        Sort By
      </Text>
      <SimpleBar
        style={{
          maxHeight: 'calc(100vh - 400px)',
        }}
      >
        <SortableList
          overflowY="scroll"
          items={sortedColumns}
          renderItem={item => {
            const isLast =
              sortedColumns.findIndex(col => col.id === item.id) ===
              sortedColumns.length - 1
            return (
              <SortableItem
                id={item.id}
                gap="8px"
                pr="16px"
                {...(isLast ? { pb: 0 } : {})}
              >
                <SelectInput
                  options={filterFieldsSelectInputOptions.filter(
                    option =>
                      sortedColumns.find(
                        col => col.propertyKey === option.value,
                      ) === undefined,
                  )}
                  onChange={option => {
                    const newPropertyKey = option?.value!
                    const currentDirection = getDirectionFromContentNode(
                      item.contentNode,
                      item.propertyKey,
                    )
                    handleSetSortedItems(
                      sortedColumns.map(col => {
                        if (item.id === col.id) {
                          const newNode = createSortContentNode(
                            orderByInputObjectConfig,
                            newPropertyKey,
                            currentDirection,
                          )
                          return {
                            ...col,
                            id: `${newPropertyKey}-${col.sortOrder - 1}`,
                            propertyKey: newPropertyKey,
                            contentNode: newNode,
                          }
                        }
                        return col
                      }),
                    )
                  }}
                  value={
                    filterFieldsSelectInputOptions.find(
                      option => option.value === item.propertyKey,
                    ) ?? null
                  }
                  width="175px"
                  marginRight="2px"
                />
                <ButtonRadio
                  onChange={option => {
                    const newDirection = option === 'ascending' ? 'ASC' : 'DESC'
                    handleSetSortedItems(
                      sortedColumns.map(col => {
                        if (item.id === col.id) {
                          const newNode = createSortContentNode(
                            orderByInputObjectConfig,
                            col.propertyKey,
                            newDirection,
                          )
                          return {
                            ...col,
                            contentNode: newNode,
                          }
                        }
                        return col
                      }),
                    )
                  }}
                  options={[
                    { label: 'Ascending', value: 'ascending' },
                    { label: 'Descending', value: 'descending' },
                  ]}
                  value={
                    getDirectionFromContentNode(
                      item.contentNode,
                      item.propertyKey,
                    ) === 'ASC'
                      ? 'ascending'
                      : 'descending'
                  }
                />
                <IconButton
                  aria-label="Remove sort"
                  padding="8px"
                  icon={<XIcon boxSize="12px" color="presets.neutral.100" />}
                  onClick={() =>
                    handleSetSortedItems(
                      sortedColumns.filter(col => {
                        return item.id !== col.id
                      }),
                    )
                  }
                />
              </SortableItem>
            )
          }}
        />
      </SimpleBar>
      {sortedColumns.length < filterFieldsSelectInputOptions.length && (
        <Button
          size="sm"
          variant="tertiary"
          marginLeft="54px"
          onClick={() => {
            const availablePropertyKey = filterFieldsSelectInputOptions.find(
              option =>
                sortedColumns.find(col => col.propertyKey === option.value) ===
                undefined,
            )?.value

            if (availablePropertyKey) {
              const newNode = createSortContentNode(
                orderByInputObjectConfig,
                availablePropertyKey,
                'ASC',
              )
              handleSetSortedItems([
                ...sortedColumns,
                {
                  id: `${availablePropertyKey}-${sortedColumns.length}`,
                  propertyKey: availablePropertyKey,
                  contentNode: newNode,
                  sortOrder: sortedColumns.length + 1,
                },
              ])
            }
          }}
        >
          + {sortedColumns.length > 0 ? 'Add Another Sort' : 'Add a Sort'}
        </Button>
      )}
    </DndContext>
  )
}
